import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  CARE_POLICIES, CATEGORIES, LAUNDRY_GROUPS, LAYERS, type CategoryDefaults,
} from '@wardrobe/shared';
import { api } from '../api.js';
import {
  Abschnitt, Button, Card, EINGABE, ErrorBox, Feld, Loading, Screen, Toggle,
  carePolicyLabel, categoryLabel, laundryGroupLabel, layerLabel,
} from '../ui.js';

type Quelle = 'katalog' | 'bestand' | 'gesetzt';
type Startwerte = CategoryDefaults & { quellen: Record<string, Quelle> };

const QUELLE_TEXT: Readonly<Record<Quelle, string>> = {
  katalog: 'geschätzt',
  bestand: 'aus deinem Bestand',
  gesetzt: 'von dir gesetzt',
};

/** Nach der deutschen Beschriftung sortiert — so sucht man in einer Liste. */
const KATEGORIEN = [...CATEGORIES].sort((a, b) =>
  categoryLabel(a).localeCompare(categoryLabel(b), 'de'),
);

/**
 * D-41: Die Kategorie-Startwerte sind Daten, kein Quelltext.
 *
 * Sie stehen in drei Schichten übereinander: der geschätzte Katalog aus dem Code, der
 * eigene Bestand ab fünf Teilen (D-38), und ganz oben, was hier gesetzt wird. Der
 * Bildschirm sagt je Feld, welche Schicht gewonnen hat — sonst wäre nicht zu erkennen,
 * ob eine Zahl gemessen, gelernt oder geraten ist.
 */
export function Categories({ navigate }: { navigate: (to: string) => void }): ReactNode {
  const [offen, setOffen] = useState<string | null>(null);
  const startwerte = useQuery({ queryKey: ['defaults'], queryFn: api.defaults });

  if (startwerte.isLoading) return <Loading />;
  const alle = (startwerte.data ?? {}) as Record<string, Startwerte>;

  if (offen !== null) {
    const werte = alle[offen];
    if (werte !== undefined) {
      return <Bearbeiten category={offen} werte={werte} onFertig={() => setOffen(null)} />;
    }
  }

  return (
    <Screen
      title="Kategorie-Startwerte"
      subtitle="Was ein neues Teil mitbekommt"
      action={<Button variant="ghost" onClick={() => navigate('/mehr')}>Zurück</Button>}
    >
      <Card className="mb-4">
        <p className="text-[13px] leading-relaxed text-white/55">
          Die Werte im Katalog sind pragmatische Schätzungen, keine gemessenen. Sobald du
          fünf Teile einer Kategorie hast, gilt stattdessen dein eigener Mittelwert — und
          was du hier einträgst, sticht beides.
        </p>
      </Card>

      <div className="space-y-2">
        {KATEGORIEN.map((c) => {
          const werte = alle[c];
          const gesetzt = Object.values(werte?.quellen ?? {}).filter((q) => q === 'gesetzt').length;
          const bestand = Object.values(werte?.quellen ?? {}).filter((q) => q === 'bestand').length;
          return (
            <button
              key={c}
              onClick={() => setOffen(c)}
              className="press flex w-full items-center justify-between gap-3 rounded-[var(--radius-card)] bg-panel p-3.5 text-left ring-1 ring-white/5"
            >
              <span className="font-medium">{categoryLabel(c)}</span>
              <span className="shrink-0 text-[11px] text-white/40">
                {gesetzt > 0
                  ? `${gesetzt} von dir gesetzt`
                  : bestand > 0
                    ? `${bestand} aus deinem Bestand`
                    : 'geschätzt'}
              </span>
            </button>
          );
        })}
      </div>
      <ErrorBox error={startwerte.error} />
    </Screen>
  );
}

function Bearbeiten({ category, werte, onFertig }: {
  category: string;
  werte: Startwerte;
  onFertig: () => void;
}): ReactNode {
  const qc = useQueryClient();
  const [patch, setPatch] = useState<Record<string, unknown>>({});
  const setzen = (feld: string, wert: unknown): void => setPatch((p) => ({ ...p, [feld]: wert }));

  const aktuell = (feld: keyof CategoryDefaults): unknown =>
    feld in patch ? patch[feld] : werte[feld];
  const text = (feld: keyof CategoryDefaults): string => {
    const wert = aktuell(feld);
    if (typeof wert === 'string') return wert;
    return typeof wert === 'number' ? String(wert) : '';
  };
  const zahl = (feld: keyof CategoryDefaults): number => {
    const wert = aktuell(feld);
    return typeof wert === 'number' ? wert : 0;
  };
  const setzenZahl = (feld: string, roh: string, min: number, max: number): void => {
    const n = Number.parseFloat(roh.replace(',', '.'));
    setzen(feld, Number.isNaN(n) ? min : Math.min(max, Math.max(min, n)));
  };

  const erledigt = (): void => {
    void qc.invalidateQueries({ queryKey: ['defaults'] });
    onFertig();
  };
  const speichern = useMutation({
    mutationFn: () => api.setCategoryDefaults(category, patch),
    onSuccess: erledigt,
  });
  const zuruecksetzen = useMutation({
    mutationFn: () => api.clearCategoryDefaults(category),
    onSuccess: erledigt,
  });

  /** Woher der Wert kommt, den das Feld gerade zeigt. */
  const herkunft = (feld: string): string =>
    feld in patch ? 'geändert, noch nicht gespeichert' : QUELLE_TEXT[werte.quellen[feld] ?? 'katalog'];

  const reinigung = aktuell('wearsPerClean') as number | null;
  const gesetzteFelder = Object.values(werte.quellen).filter((q) => q === 'gesetzt').length;

  return (
    <Screen
      title={categoryLabel(category)}
      subtitle="Startwerte für neue Teile"
      action={<Button variant="ghost" onClick={onFertig}>Zurück</Button>}
    >
      <Card>
        <p className="text-[13px] text-white/45">
          Gilt für Teile, die du ab jetzt anlegst. Vorhandene Teile bleiben, wie sie sind —
          die änderst du am Teil selbst.
        </p>

        <Feld label="Schicht" hinweis={herkunft('layer')}>
          <select value={text('layer')} onChange={(e) => setzen('layer', e.target.value)} className={EINGABE}>
            {LAYERS.map((l) => (
              <option key={l} value={l}>{layerLabel(l)}</option>
            ))}
          </select>
        </Feld>

        <div className="grid grid-cols-2 gap-3">
          <Feld label="Wärme (0–10)" hinweis={herkunft('warmthPoints')}>
            <input value={zahl('warmthPoints')} inputMode="numeric" onChange={(e) => setzenZahl('warmthPoints', e.target.value, 0, 10)} className={`tnum ${EINGABE}`} />
          </Feld>
          <Feld label="Formalität (1–5)" hinweis={herkunft('formality')}>
            <input value={zahl('formality')} inputMode="numeric" onChange={(e) => setzenZahl('formality', e.target.value, 1, 5)} className={`tnum ${EINGABE}`} />
          </Feld>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Feld label="Regen (0–3)" hinweis={herkunft('rainResistance')}>
            <input value={zahl('rainResistance')} inputMode="numeric" onChange={(e) => setzenZahl('rainResistance', e.target.value, 0, 3)} className={`tnum ${EINGABE}`} />
          </Feld>
          <Feld label="Wind (0–3)" hinweis={herkunft('windResistance')}>
            <input value={zahl('windResistance')} inputMode="numeric" onChange={(e) => setzenZahl('windResistance', e.target.value, 0, 3)} className={`tnum ${EINGABE}`} />
          </Feld>
        </div>

        <Abschnitt titel="Pflege" />
        <div className="grid grid-cols-2 gap-3">
          <Feld label="Pflege" hinweis={herkunft('carePolicy')}>
            <select value={text('carePolicy')} onChange={(e) => setzen('carePolicy', e.target.value)} className={EINGABE}>
              {CARE_POLICIES.map((p) => (
                <option key={p} value={p}>{carePolicyLabel(p)}</option>
              ))}
            </select>
          </Feld>
          <Feld label="Wäschegruppe" hinweis={herkunft('laundryGroup')}>
            <select value={text('laundryGroup')} onChange={(e) => setzen('laundryGroup', e.target.value)} className={EINGABE}>
              {LAUNDRY_GROUPS.map((g) => (
                <option key={g} value={g}>{laundryGroupLabel(g)}</option>
              ))}
            </select>
          </Feld>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Feld label="Wäsche nach Tragungen" hinweis={herkunft('wearsPerWash')}>
            <input value={zahl('wearsPerWash')} inputMode="numeric" onChange={(e) => setzenZahl('wearsPerWash', e.target.value, 1, 100)} className={`tnum ${EINGABE}`} />
          </Feld>
          <Feld label="Trockenzeit (h)" hinweis={herkunft('dryingHours')}>
            <input value={zahl('dryingHours')} inputMode="decimal" onChange={(e) => setzenZahl('dryingHours', e.target.value, 0, 200)} className={`tnum ${EINGABE}`} />
          </Feld>
        </div>
        <Feld label="Ruhezeit (h)" hinweis={`${herkunft('restHours')} · So lange gilt ein Teil nach dem Tragen als nicht verfügbar.`}>
          <input value={zahl('restHours')} inputMode="decimal" onChange={(e) => setzenZahl('restHours', e.target.value, 0, 500)} className={`tnum ${EINGABE}`} />
        </Feld>

        <p className="mt-3 text-xs text-white/55">Reinigung</p>
        <div className="mt-1 flex gap-2">
          <button
            onClick={() => setzen('wearsPerClean', null)}
            className={`flex-1 rounded-full px-3 py-2 text-sm ${reinigung === null ? 'bg-white text-black' : 'bg-white/8 text-white/75'}`}
          >
            Nur bei Bedarf
          </button>
          <button
            onClick={() => setzen('wearsPerClean', reinigung ?? 15)}
            className={`flex-1 rounded-full px-3 py-2 text-sm ${reinigung !== null ? 'bg-white text-black' : 'bg-white/8 text-white/75'}`}
          >
            Nach Tragungen
          </button>
        </div>
        {reinigung !== null && (
          <input
            value={reinigung}
            inputMode="numeric"
            onChange={(e) => setzenZahl('wearsPerClean', e.target.value, 1, 200)}
            className={`tnum ${EINGABE}`}
          />
        )}

        <Toggle
          label="Bügeln"
          hint={herkunft('needsIroning')}
          value={aktuell('needsIroning') === true}
          onChange={(v) => setzen('needsIroning', v)}
        />
        <Toggle
          label="Für Vorschläge berücksichtigen"
          hint={`${herkunft('suggestable')} · Aus bei Socken und Unterwäsche: getragen und gewaschen, aber nie zur Wahl gestellt.`}
          value={aktuell('suggestable') === true}
          onChange={(v) => setzen('suggestable', v)}
        />

        <div className="mt-5 flex gap-2">
          <Button
            className="flex-1"
            disabled={speichern.isPending || Object.keys(patch).length === 0}
            onClick={() => speichern.mutate()}
          >
            {Object.keys(patch).length === 0 ? 'Nichts geändert' : 'Speichern'}
          </Button>
          <Button variant="ghost" onClick={onFertig}>Abbrechen</Button>
        </div>

        {gesetzteFelder > 0 && (
          <Button
            variant="ghost"
            className="mt-2 w-full"
            disabled={zuruecksetzen.isPending}
            onClick={() => zuruecksetzen.mutate()}
          >
            Eigene Werte verwerfen
          </Button>
        )}
        <ErrorBox error={speichern.error ?? zuruecksetzen.error} />
      </Card>
    </Screen>
  );
}
